import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { Flex, RemoteImage, ColorValues } from '@banhala/ui-kit';
import type { DescriptionCardItem } from '../types';
import type { AnyObject } from '../../../../../types';
import { bridge, renderHTMLToText, convertDeeplinkToWebLink, getWebBaseURL } from '../../../../../utils';
import { ABLY_INFO } from '../../../../../constant/ablyInfo';

const CARD_WIDTH = 140;
const CARD_IMAGE_HEIGHT = 168;
const DESCRIPTION_ROW_HEIGHT = 44;
const CARD_BORDER_RADIUS = 4;

interface DescriptionCardProps {
  item: DescriptionCardItem;
  logging: AnyObject;
}

export const DescriptionCard = ({ item, logging }: DescriptionCardProps) => {
  const { card_image, deeplink, description } = item;

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_DESCRIPTION_CARD',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          const convertedLink = convertDeeplinkToWebLink(deeplink);
          if (convertedLink === ABLY_INFO.url) {
            location.href = ABLY_INFO.url;
            return;
          }
          location.href = `${baseURL}${convertedLink}`;
        },
      });
    }
  }, [deeplink, logging]);

  return (
    <Flex padding={{ top: 4, bottom: 12, horizontal: 4 }}>
      <Flex
        width={CARD_WIDTH}
        borderRadius={CARD_BORDER_RADIUS}
        overflow="hidden"
        backgroundColor="bg_always_white"
        shadowStep="small"
        onPress={handlePress}
      >
        <RemoteImage
          uri={card_image}
          width={CARD_WIDTH}
          height={CARD_IMAGE_HEIGHT}
        />
        <Flex.Row
          height={DESCRIPTION_ROW_HEIGHT}
          alignItems="center"
          padding={{ left: 12 }}
        >
          {renderHTMLToText(description.text, {
            numberOfLines: 1,
            style: styles.descriptionText,
          })}
          {description.image?.url && (
            <RemoteImage
              uri={description.image.url}
              width={description.image.width}
              height={description.image.height}
            />
          )}
        </Flex.Row>
      </Flex>
    </Flex>
  );
};

const styles = StyleSheet.create({
  descriptionText: {
    flexShrink: 1,
    color: ColorValues.slate50,
    fontSize: 14,
    lineHeight: 18,
    letterSpacing: -0.4,
  },
});
